import type { CategoriesApiProvider } from '../../../../api/services/categories.service';
import type { DatabaseViewApiProvider } from '../../../../api/services/database-view.service';
import type { DisabilityTypeService } from '../../../../api/services/disability-type.api';
import type { IntegrationServiceOfficeService } from '../../../../api/services/integration-office.service';
import type { LegalFoundationApi } from '../../../../api/services/legal-foundation.service';
import type { UserApi } from '../../../../api/services/user.service';
import type { DatabaseFilterOption } from '../models/database-view-storage.types';
import { sanitizeFilterOptionEntries } from './database-filter-state';

export const DATABASE_FILTER_SOURCE_KEYS = [
	'typeOfCare',
	'caseGroup',
	'initiator',
	'legalFoundation',
	'assignmentSponsor',
	'disabilityType',
	'integrationServiceOffice',
	'service',
	'advisorName',
] as const;

export type DatabaseFilterSourceKey = (typeof DATABASE_FILTER_SOURCE_KEYS)[number];

export const nameOptions = (names: string[]): DatabaseFilterOption[] =>
	names.map((name) => ({ value: name, label: name }));

export interface DatabaseFilterSourceServices {
	categoriesApi: Pick<CategoriesApiProvider, 'getAllTypesOfCare' | 'getAllCaseGroups' | 'getAllPersonTypes'>;
	legalFoundationApi: Pick<LegalFoundationApi, 'getAll'>;
	databaseViewApi: Pick<DatabaseViewApiProvider, 'filterSponsors'>;
	disabilityTypeApi: Pick<DisabilityTypeService, 'getAll'>;
	isoService: Pick<IntegrationServiceOfficeService, 'getAll'>;
	userService: Pick<UserApi, 'getAll'>;
}

const ADVISOR_OPTIONS_OFFSET = 0;
const ADVISOR_OPTIONS_RANGE = 500;

/**
 * Loads the dropdown options that the Aufträge tab uses, so other database lists can share the same sources.
 * Only the requested keys are fetched; IFD and service share one isos request.
 * Each source is requested independently; a failed source yields `null` instead of rejecting.
 */
export const loadDatabaseFilterSourceOptions = async <TKey extends DatabaseFilterSourceKey>(
	services: DatabaseFilterSourceServices,
	offset: number,
	range: number,
	keys?: readonly TKey[],
): Promise<Record<TKey, DatabaseFilterOption[] | null>> => {
	const requestedKeys = keys ?? DATABASE_FILTER_SOURCE_KEYS;
	let isosRequest: ReturnType<IntegrationServiceOfficeService['getAll']> | undefined;
	const loadIsos = () => (isosRequest ??= services.isoService.getAll(offset, range));

	const loaders: Record<DatabaseFilterSourceKey, () => Promise<DatabaseFilterOption[]>> = {
		typeOfCare: async () =>
			nameOptions(
				(await services.categoriesApi.getAllTypesOfCare(offset, range, false, false, true)).typesOfCare.map(
					(x) => x.initial,
				),
			),
		caseGroup: async () =>
			nameOptions(
				(await services.categoriesApi.getAllCaseGroups(offset, range)).caseGroups.map((x) => x.description || ''),
			),
		initiator: async () =>
			nameOptions(
				(await services.categoriesApi.getAllPersonTypes(offset, range, 'IsInitiator')).entities
					.filter((x) => x.isInitiator)
					.map((x) => x.name),
			),
		legalFoundation: async () =>
			nameOptions((await services.legalFoundationApi.getAll(offset, range)).entities.map((x) => x.description)),
		assignmentSponsor: async () =>
			nameOptions((await services.databaseViewApi.filterSponsors(offset, range)).entities.map((x) => x.name)),
		disabilityType: async () =>
			nameOptions((await services.disabilityTypeApi.getAll(offset, range)).entities.map((x) => x.remark)),
		integrationServiceOffice: async () =>
			nameOptions((await loadIsos()).entities.map((ifd) => ifd.shortName || ifd.remark || ifd.initial || '')),
		service: async () =>
			nameOptions((await loadIsos()).entities.flatMap((ifd) => ifd.services?.map((service) => service.name) ?? [])),
		advisorName: async () =>
			(
				await services.userService.getAll({
					offset: ADVISOR_OPTIONS_OFFSET,
					range: ADVISOR_OPTIONS_RANGE,
					activeUsers: true,
					sortBy: 'firstName',
					ascending: true,
				})
			).entities
				.filter((user) => user.isActive)
				.map((user) => ({
					value: user.id,
					label: [user.firstName, user.lastName]
						.map((part) => part.trim())
						.filter(Boolean)
						.join(' '),
				})),
	};

	const results = await Promise.allSettled(requestedKeys.map((key) => loaders[key]()));
	return Object.fromEntries(
		requestedKeys.map((key, index) => {
			const result = results[index];
			return [key, result.status === 'fulfilled' ? sanitizeFilterOptionEntries(result.value) : null];
		}),
	) as Record<TKey, DatabaseFilterOption[] | null>;
};
